/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { formatISO } from 'date-fns';

import { isWeb } from '@global/screens';
import Image from '@components/global/Image';
import StatusBadge from '@components/global/StatusBadge';
import SociollaText from '@components/sociolla/SociollaText';
import SociollaTable from '@components/sociolla/SociollaTable';
import ModalFull from '@components/modal/ModalFull';
import { MsOrder } from '@api/ms-order';
import tableApi from '@/hooks/tableApi';
import Colors from '@global/colors';
import { parseDate, changeTimeZone } from '@global/helpers';
import PropTypes from 'prop-types';

const propTypes = {
  show: PropTypes.bool,
};

const defaultProps = {
  show: false,
};

const HEADER_DEKSTOP = [
  {
    label: 'Rules Name',
    data: 'name',
    width: '25%',
    numberOfLines: 2,
  },
  {
    label: 'Promo Type',
    data: 'promotion_type',
    width: '15%',
    numberOfLines: 2,
  },
  {
    label: 'Periode',
    data: 'periode',
    width: '25%',
    numberOfLines: 2,
  },
  {
    label: 'Status',
    data: 'is_active',
    width: '15%',
    type: 'status_active',
    numberOfLines: 2,
  },
  {
    label: 'Last Updated By',
    data: 'last_updated',
    width: '20%',
    numberOfLines: 2,
  },
];

const SelectPromotionTable = (props) => {
  const { show = false } = props;

  const tableRef = useRef();

  const [emptyLabel, setEmptyLabel] = useState('Please search any promotion.');
  const [paramsTable, setParamsTable] = useState({
    filter: {},
    limit: isWeb ? 10 : 15,
    skip: 0,
  });
  const [list, setList] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const promotionTypes = useSelector((state) => state.global.promotionTypes);

  const promotions = tableApi(
    MsOrder.getPromotionRulesCount,
    MsOrder.getPromotionRules,
  );

  const callBackRowPress = (item) => {
    // eslint-disable-next-line react/prop-types
    props.onCallback(item.item);
  };

  const onSearchTable = async (param, type) => {
    const params = { ...paramsTable };
    if (type === 'keyword') {
      if (param.keyword.length > 2) {
        params.filter['name'] = { $regex: param.keyword, $options: 'i' };
      } else if (!param.keyword) {
        delete params.filter['name'];
        setEmptyLabel('Please search any promotion.');
        setList([]);
        return;
      }
    } else if (type === 'pagination') {
      setLoadingMore(true);
      params.limit = param.limit;
      params.skip = param.skip;
    }
    params.filter['is_active'] = true;
    const today = new Date();
    let start_date = today.setHours(0);
    start_date = today.setMinutes(0);
    start_date = today.setSeconds(0);
    let end_date = today.setHours(23);
    end_date = today.setMinutes(59);
    end_date = today.setSeconds(59);
    params.filter['from_date'] = {
      $lte: formatISO(changeTimeZone(new Date(start_date))),
    };
    params.filter['to_date'] = {
      $gte: formatISO(changeTimeZone(new Date(end_date))),
    };
    await setParamsTable({ ...params });
    apiPromotion(params);
  };

  const apiPromotion = (params = null) => {
    const param = params ? params : { ...paramsTable };
    const hasFilter = Object.keys(param?.filter || {}).length ? true : false;
    promotions.request(param, null, hasFilter).then((response) => {
      if (response) {
        if (!response.length) {
          setEmptyLabel('No data found.');
        }
        if (param.skip > 0) {
          setList([...list, ...generateData(response)]);
        } else {
          setList(generateData(response));
        }
        setLoadingMore(false);
      }
    });
  };

  const generateData = (data) => {
    const result = [];
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      if (item._id) {
        item.id = item._id;
      }

      item.promotion_type = item.promotion_type
        ? promotionTypes[item.promotion_type]
        : '-';

      item.periode =
        item.from_date || item.to_date
          ? `${
              item.from_date
                ? parseDate(
                    item.from_date,
                    isWeb ? 'd MMMM yyyy' : 'd MMM yyyy',
                  )
                : '-'
            } - ${
              item.to_date
                ? parseDate(item.to_date, isWeb ? 'd MMMM yyyy' : 'd MMM yyyy')
                : '-'
            }`
          : '-';

      item.last_updated =
        item.updated_at || item.updated_by
          ? `${item.updated_at ? parseDate(item.updated_at) : '-'} | ${
              item?.updated_by?.name ? item.updated_by.name : '-'
            }`
          : '-';

      result.push(item);
    }
    return result;
  };

  const renderItem = ({ item, index }) => (
    <TouchableOpacity
      onPress={() => callBackRowPress({ item })}
      key={'list-product-' + index}
      style={styles.tableRow}>
      <View style={styles.promotionItem} key={index}>
        <View style={[styles.promotionItemSection, { marginBottom: 10 }]}>
          <SociollaText type="medium" style={{ fontSize: 15 }}>
            {item.name ? item.name : '-'}
          </SociollaText>
          <StatusBadge status={item.is_active ? 'active' : 'in_active'} />
        </View>
        <View style={styles.promotionItemSection}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Image
              style={{
                width: 19,
                height: 14,
                marginLeft: -5,
                marginRight: 5,
              }}
              width={19}
              height={14}
              svg={true}
              source={require('@assets/img/icons/menu/calendar.svg')}
            />
            <SociollaText style={{ fontSize: 11, color: Colors.grey2 }}>
              {item.periode ? item.periode : '-'}
            </SociollaText>
          </View>
          <SociollaText style={{ fontSize: 11, color: Colors.grey1 }}>
            Type: {item.promotion_type}
          </SociollaText>
        </View>
      </View>
    </TouchableOpacity>
  );

  const onClose = () => {
    try {
      // eslint-disable-next-line react/prop-types
      props.onClose();
    } catch (err) {}
  };

  useEffect(() => {
    let mounted = true;
    if (mounted && show) {
      onSearchTable(paramsTable, '');
    }
    return () => {
      mounted = false;
    };
  }, [show]);

  return (
    <ModalFull
      title={'Select Promotion Rule'}
      show={show ? true : false}
      onClose={() => onClose()}
      width={isWeb ? 1000 : '100%'}>
      <View style={styles.tableContainer}>
        {isWeb && (
          <SociollaTable
            table={HEADER_DEKSTOP}
            ref={tableRef}
            datas={list}
            showSearch={true}
            searchBorder={true}
            scrollItem={false}
            maxHeight={320}
            stickyHeaderIndices={[0]}
            searchPlaceholder="Search by name"
            emptyLabel={emptyLabel}
            count={promotions.count}
            loading={promotions.loading}
            loadingLoadMore={loadingMore}
            callBackRowPress={callBackRowPress}
            onSearchTable={(val, type) => onSearchTable(val, type)}
            localParams={paramsTable}
          />
        )}
        {!isWeb && (
          <SociollaTable
            datas={list}
            itemComponent={renderItem}
            headerComponent={<></>}
            showSearch={true}
            loading={promotions.loading}
            count={promotions.count}
            scrollItem={true}
            loadingLoadMore={loadingMore}
            searchPlaceholder="Search by name"
            onSearchTable={(val, type) => onSearchTable(val, type)}
            localParams={paramsTable}
          />
        )}
      </View>
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  tableRow: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    height: 90,
    width: '100%',
    flex: 1,
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    paddingHorizontal: 20,
  },
  tableContainer: isWeb
    ? {
        backgroundColor: Colors.grey11,
        paddingHorizontal: 35,
        paddingVertical: 20,
      }
    : { flex: 1, backgroundColor: Colors.white },
  promotionItem: {
    width: '100%',
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  promotionItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

SelectPromotionTable.propTypes = propTypes;
SelectPromotionTable.defaultProps = defaultProps;

export default React.memo(SelectPromotionTable, (prev, next) => prev == next);
